import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import ReorderIcon from "@mui/icons-material/Reorder";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import AddTenant from "../forms/AddTenant";
import { LightTooltip } from "../tooltip/ToolTip";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import EditIcon from "@mui/icons-material/Edit";
import AddPopup from "../popup/AddPopup";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

const Tenants = () => {
  const [isFetchingTenants, setIsFetchingTenants] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [editTenant, setEditTenant] = useState(false);
  const [viewTenant, setViewTenant] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshTenants, setRefreshTenants] = useState(null);

  const TENANTS_URL = "api/v1/tenants";
  const axiosPrivate = useAxiosPrivate();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(TENANTS_URL + "/" + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setRefreshTenants(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Tenant",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingTenants) {
      setIsFetchingTenants(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getTenants = async () => {
      try {
        const response = await axiosPrivate.get(
          TENANTS_URL,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setTenants(response.data.data);
        setIsFetchingTenants(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingTenants(false);
      }
    };

    getTenants();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshTenants]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "tenantNumber",
        headerName: "Number",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        headerClassName: "super-app-theme--header",
        minWidth: 170,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "email",
        headerName: "Email",
        headerClassName: "super-app-theme--header",
        minWidth: 220,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "phone",
        headerName: "Phone",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "locations",
        headerName: "Branches",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const locations = params.row.locations;
          return locations?.map((elem) => elem.name).join(",");
        },
      },
      {
        field: "specialities",
        headerName: "Speciality",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const specialities = params.row.specialities;
          return specialities?.map((elem) => elem.name).join(",");
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 81,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit tenant">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedTenant = JSON.stringify(currentRow, null, 4);
                  setViewTenant(false);
                  setEditTenant(true);
                  setSelectedTenant(selectedTenant);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete tenant">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this tenant?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
            <GridActionsCellItem
              icon={<ReorderIcon />}
              label={"View Tenant"}
              onClick={function () {
                const currentRow = params.row;
                const selectedTenant = JSON.stringify(currentRow, null, 4);
                setViewTenant(true);
                setEditTenant(true); // You might want to edit tenant under view tenant
                setSelectedTenant(selectedTenant);
                return setOpenPopup(true);
              }}
              color="inherit"
              showInMenu
            />,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setRefreshTenants(new Date());
  };

  return isFetchingTenants ? (
    <PageLoader title="Loading tenants..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {tenants?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Tenants
            </Typography>

            <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                setEditTenant(false);
                setSelectedTenant(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Add Tenant
            </Button>
          </div>
          <Box
            sx={{
              //width: { xs: 480, sm: 480, md: 740, lg: 1218, xl: 1218 }, // xs=iPhone SE; sm=iPad Mini; md=iPad pro; lg=Nest Hub Max
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={tenants} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewTenant
                ? "View (Edit) Tenant"
                : editTenant
                ? "Edit (View) Tenant"
                : "Add Tenant"
            }
          >
            <AddTenant
              closeDialog={closeDialog}
              editTenant={editTenant}
              selectedTenant={selectedTenant}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No tenants to display
          </Typography>
          <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              setEditTenant(false);
              setSelectedTenant(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Add Tenant
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewTenant
                ? "View (Edit) Tenant"
                : editTenant
                ? "Edit (View) Tenant"
                : "Add Tenant"
            }
          >
            <AddTenant
              closeDialog={closeDialog}
              editTenant={editTenant}
              selectedTenant={selectedTenant}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Tenants;
